import * as React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepButton,
  Button,
  Typography,
  Container,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import Step1Form from './Step1Form';
import Step2Form from './Step2Form';
import Step3Form from './Step3Form';
import Step4Form from './Step4Form';
import CreateEventAdmin from '../CreateEventAdmin';

import Tooltip from '@mui/material/Tooltip';
import { useTheme } from "@mui/material";
import getUserRoleFlags from '../../hooks/useGetUserRoleFlags';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneIcon from '@mui/icons-material/Done';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NumberRangeOverviewDialog from '../../components/NumberRangeOverviewDialog';
import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreatorWorkspaceApi } from "../../hooks/useCreatorWorkspaceApi";
import { useEventsApi } from "../../hooks/useEventsApi";
import { getNumberRangeBaseFunctions } from "../../shared/numberRangeBasesCache";
import { parseRisingCodeBandDigits } from "../../shared/eventCodeRange";
import { config } from "../../config";
import CreatorContextBandBar from "../../components/CreatorContextBandBar";

const steps = [
  'Base function',
  'Sub function',
  'Event creation mode',
  'Event data (rising)',
  'Event data (resolving)',
  'Conclusion',
];
type StepId = 0 | 1 | 2 | 3 | 4 | 5;

const STEPPER_LABEL_TOOLTIPS: Partial<Record<number, string>> = {
  2:
    "Choose whether you create a rising event only, a resolving event linked to an existing rising event, or both as a matched pair.",
  3:
    "Rising-edge event: fires when a condition becomes active (e.g. safety scanner detects something in the zone). Optional resolving event clears it later.",
  4:
    "Resolving event: fires when the condition clears (e.g. zone free again). Here you also link an existing rising event if you chose Resolving only.",
};

const buildSuggestedSessionTitle = (
  eventCreation: string,
  formData: FormDataState
): string => {
  const baseId = formData.step1?.selectedBaseFunctionId;
  const baseName = (formData.step1 as any)?.selectedBaseFunctionName;
  const subId = (formData.step2 as any)?.selectedSubFunction;
  const subName = (formData.step2 as any)?.selectedSubFunctionName;

  const candidates = [formData.step3a, formData.step3b, formData.step4] as any[];
  const pickString = (keys: string[]): string => {
    for (const source of candidates) {
      if (!source || typeof source !== "object") continue;
      for (const key of keys) {
        const raw = source[key];
        if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
      }
      const attrs = source.attributes;
      if (attrs && typeof attrs === "object") {
        for (const key of keys) {
          const raw = attrs[key];
          if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
        }
      }
    }
    return "";
  };
  const pickNumber = (keys: string[]): number | null => {
    for (const source of candidates) {
      if (!source || typeof source !== "object") continue;
      for (const key of keys) {
        const raw = source[key];
        const num = Number(raw);
        if (Number.isFinite(num) && num > 0) return num;
      }
    }
    return null;
  };

  const modeLabel =
    eventCreation === "rising"
      ? "Rising"
      : eventCreation === "resolving"
        ? "Resolving"
        : eventCreation === "both"
          ? "Rising+Resolving"
          : "Draft";
  const eventCode = pickNumber(["event_code", "eventCode"]);
  const contractName = pickString(["event_contract_name", "eventContractName"]);
  const parts: string[] = [`${modeLabel} creator`];
  if (typeof baseName === "string" && baseName.trim().length > 0) {
    parts.push(baseName.trim().slice(0, 30));
  } else if (typeof baseId === "number" && baseId > 0) {
    parts.push(`B${baseId}`);
  }
  if (typeof subName === "string" && subName.trim().length > 0) {
    parts.push(subName.trim().slice(0, 30));
  } else if (typeof subId === "number" && subId > 0) {
    parts.push(`S${subId}`);
  }
  if (eventCode != null) parts.push(`#${eventCode}`);
  if (contractName) parts.push(`- ${contractName.slice(0, 28)}`);
  return parts.join(" ");
};

interface Step1Data {
  selectedBaseFunctionId?: number;
  selectedBaseFunctionName?: string;
  selectedBaseDigit?: number;
}

interface Step2Data {
  selectedSubFunction?: number;
  selectedSubFunctionName?: string;
  selectedBaseFunctionName?: string;
  selectedSubDigit?: number;
}

interface FormDataState {
  step1: Step1Data;
  step2: Step2Data;
  step3a: any;
  step3b: any;
  step4: any;
}

const initialCreatorFormData: FormDataState = {
  step1: {},
  step2: {},
  step3a: {},
  step3b: {},
  step4: {},
};

export default function EventConfigurator() {
  const { isAdmin } = getUserRoleFlags();
  const { hasUnsavedChanges, setHasUnsavedChanges, setDiscardDraftHandler } = useUnsavedChanges();
  const location = useLocation();
  const navigate = useNavigate();
  const { createSession, updateSession, deleteSession, listMySessions } = useCreatorWorkspaceApi();
  const { callApi } = useEventsApi();
  const callApiRef = React.useRef(callApi);
  callApiRef.current = callApi;
  const { accounts } = useMsal();
  const creatorPrefetchAccountKey = accounts[0]?.homeAccountId ?? "";
  const [activeStep, setActiveStep] = React.useState(0);
  const [maxStep, setMaxStep] = React.useState(0);
  const [eventCreation, setEventCreation] = React.useState("none");
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({});
  const [stepValid, setStepValid] = React.useState<{ [k: number]: boolean }>({});
  const [formData, setFormData] = React.useState<FormDataState>(initialCreatorFormData);
  const [workspaceSessionId, setWorkspaceSessionId] = React.useState<number | null>(null);
  /** Bump to remount wizard steps so child components (e.g. conclusion success) lose internal state after reset. */
  const [wizardSessionKey, setWizardSessionKey] = React.useState(0);
  const [workspaceTitle, setWorkspaceTitle] = React.useState("Draft creator");
  const [workspaceTitleAuto, setWorkspaceTitleAuto] = React.useState(true);
  const [workspaceSaveBusy, setWorkspaceSaveBusy] = React.useState(false);
  const [workspaceSaveError, setWorkspaceSaveError] = React.useState<string | null>(null);

  const theme = useTheme();
  const [adminMode, setAdminMode] = React.useState(false);

  React.useEffect(() => {
    if (!accounts[0]) return;
    void getNumberRangeBaseFunctions((url, method) => callApiRef.current!(url, method));
  }, [creatorPrefetchAccountKey]);

  const contextBaseDigit = formData.step1?.selectedBaseDigit ?? null;
  const contextSubDigit = formData.step2?.selectedSubDigit ?? null;
  const contextBaseName = formData.step1?.selectedBaseFunctionName ?? null;
  const contextSubName = formData.step2?.selectedSubFunctionName ?? null;

  React.useEffect(() => {
    const baseId = formData.step1?.selectedBaseFunctionId;
    if (!baseId || formData.step1?.selectedBaseDigit != null || !callApi) return;
    void getNumberRangeBaseFunctions((url, method) => callApi(url, method)).then((bases) => {
      const match = bases.find((b) => b.id === baseId);
      if (match == null) return;
      setFormData((prev) => ({
        ...prev,
        step1: { ...prev.step1, selectedBaseDigit: match.digit },
      }));
    });
  }, [formData.step1?.selectedBaseFunctionId, formData.step1?.selectedBaseDigit, callApi]);

  React.useEffect(() => {
    const baseId = formData.step1?.selectedBaseFunctionId;
    const subId = formData.step2?.selectedSubFunction;
    if (!baseId || !subId || formData.step2?.selectedSubDigit != null || !callApi) return;
    void callApi(
      `${config.apiUrl}/api/number-range-sub-function/?base=${baseId}&page_size=100`,
      "GET"
    ).then((res: unknown) => {
      const raw = res as { results?: { id: number; digit: number }[] };
      const list = Array.isArray(raw?.results) ? raw.results : [];
      const match = list.find((s) => s.id === subId);
      if (match == null) return;
      setFormData((prev) => ({
        ...prev,
        step2: { ...prev.step2, selectedSubDigit: match.digit },
      }));
    });
  }, [
    formData.step1?.selectedBaseFunctionId,
    formData.step2?.selectedSubFunction,
    formData.step2?.selectedSubDigit,
    callApi,
  ]);

  const visibleStepIds = React.useMemo<StepId[]>(() => {
    if (eventCreation === "rising") return [0, 1, 2, 3, 5];
    if (eventCreation === "resolving") return [0, 1, 2, 4, 5];
    return [0, 1, 2, 3, 4, 5];
  }, [eventCreation]);

  const totalSteps = () => visibleStepIds.length;
  const completedSteps = () => visibleStepIds.filter((id) => completed[id]).length;
  const activeVisibleIndex = Math.max(0, visibleStepIds.indexOf(activeStep as StepId));
  const maxVisibleStep = Math.max(
    0,
    visibleStepIds.reduce((acc, id, idx) => (id <= maxStep ? idx : acc), -1)
  );
  const isLastStep = () => activeVisibleIndex === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = (opts?: { bypassValidation?: boolean }) => {
    setStepEnteredByUser(false);

    if (!opts?.bypassValidation && !stepValid[activeStep]) return;

    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? visibleStepIds.find((id) => !(id in completed)) ?? activeStep
        : visibleStepIds[Math.min(activeVisibleIndex + 1, visibleStepIds.length - 1)];

    setActiveStep(newActiveStep);
    setMaxStep(newActiveStep);
  };

  const handleStep = (index: number) => () => {
    setStepEnteredByUser(true);
    setActiveStep(index);
  };

  const handleBack = () => {
    setStepEnteredByUser(true);
    const currentIdx = visibleStepIds.indexOf(activeStep as StepId);
    if (currentIdx <= 0) return;
    setActiveStep(visibleStepIds[currentIdx - 1]);
  };

  const handleComplete = () => {
    if (!stepValid[activeStep]) return;
    setCompleted({ ...completed, [activeStep]: true });
    handleNext();
  };

  const handleReset = () => {
    setWizardSessionKey((k) => k + 1);
    setActiveStep(0);
    setMaxStep(0);
    setCompleted({});
    setStepValid({});
    setStep4SubmissionDone(false);
    setQuickResolvingRisingCode(null);
    setEventCreation("none");
    setFormData(initialCreatorFormData);
    setWorkspaceTitleAuto(true);
    setWorkspaceSessionId(null);
  };

  /** Replace route state so My-Workspace session hydration does not race back in after a title/form-driven effect re-run. */
  const handleCreateAnotherEvent = useCallback(() => {
    navigate("/event-configurator", { replace: true, state: { resetCreatorAt: Date.now() } });
  }, [navigate]);
  const [stepEnteredByUser, setStepEnteredByUser] = React.useState(false);
  /** After successful POST on Conclusion (step 5), hide Back and lock stepper so the user cannot return and submit again. */
  const [step4SubmissionDone, setStep4SubmissionDone] = React.useState(false);

  /** Step 1 shortcut: jump to resolving flow with pre-filled base/sub from rising code. */
  const [quickResolvingRisingCode, setQuickResolvingRisingCode] = React.useState<string | null>(null);

  const [numberRangeOverviewOpen, setNumberRangeOverviewOpen] = React.useState(false);
  const [overviewLookupCode, setOverviewLookupCode] = React.useState<string>("");

  const openNumberRangeOverview = React.useCallback((opts?: { lookupCode?: string }) => {
    setOverviewLookupCode(opts?.lookupCode?.trim() ?? "");
    setNumberRangeOverviewOpen(true);
  }, []);

  const hasUnsavedCreatorChanges = React.useMemo(() => {
    if (step4SubmissionDone) return false;
    if (eventCreation !== "none") return true;
    return JSON.stringify(formData) !== JSON.stringify(initialCreatorFormData);
  }, [formData, eventCreation, step4SubmissionDone]);

  const hasMeaningfulCreatorData = React.useMemo(() => {
    if (eventCreation !== "none") return true;
    if ((formData.step1?.selectedBaseFunctionId ?? 0) > 0) return true;
    if ((formData.step2 as any)?.selectedSubFunction) return true;
    const textSnapshot = JSON.stringify({ step3a: formData.step3a, step3b: formData.step3b, step4: formData.step4 });
    return textSnapshot !== JSON.stringify({ step3a: {}, step3b: {}, step4: {} });
  }, [eventCreation, formData]);

  const suggestedWorkspaceTitle = React.useMemo(
    () => buildSuggestedSessionTitle(eventCreation, formData),
    [eventCreation, formData]
  );

  React.useEffect(() => {
    if (!workspaceTitleAuto) return;
    setWorkspaceTitle(suggestedWorkspaceTitle);
  }, [workspaceTitleAuto, suggestedWorkspaceTitle]);

  /** Rising + resolving: keep event type and module aligned; contract names are set independently per step. */
  React.useEffect(() => {
    if (eventCreation !== "both") return;
    const risingType = formData.step3a?.event_type ?? "UserEvent";
    const risingModule = formData.step3a?.module;
    const resolvingType = formData.step3b?.event_type ?? "UserEvent";
    const resolvingModule = formData.step3b?.module;
    if (risingType === resolvingType && risingModule === resolvingModule) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      step3b: {
        ...prev.step3b,
        event_type: risingType,
        ...(risingModule != null ? { module: risingModule } : {}),
      },
    }));
  }, [
    eventCreation,
    formData.step3a?.event_type,
    formData.step3a?.module,
    formData.step3b?.event_type,
    formData.step3b?.module,
  ]);

  React.useEffect(() => {
    const next = step4SubmissionDone ? false : hasUnsavedCreatorChanges;
    if (hasUnsavedChanges !== next) {
      setHasUnsavedChanges(next);
    }
  }, [step4SubmissionDone, hasUnsavedCreatorChanges, hasUnsavedChanges, setHasUnsavedChanges]);

  React.useEffect(() => {
    const resetAt = (location.state as { resetCreatorAt?: number } | null)?.resetCreatorAt;
    if (!resetAt) return;
    handleReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Load workspace only when location.state carries workspaceSessionId — not when suggestedWorkspaceTitle
  // changes (that re-ran this effect and restored the saved conclusion step after "Create another event").
  React.useEffect(() => {
    const targetSessionId = (location.state as { workspaceSessionId?: number } | null)?.workspaceSessionId;
    if (!targetSessionId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const sessionsResponse = await listMySessions(200, 0);
        const sessions = sessionsResponse?.results ?? [];
        if (cancelled) return;
        const match = sessions.find((s) => s.id === targetSessionId);
        if (!match) return;
        const payload = (match.payload ?? {}) as Record<string, any>;
        setWorkspaceSessionId(match.id);
        setWorkspaceTitle(match.title?.trim() ? match.title : "Draft creator");
        setWorkspaceTitleAuto(false);
        setActiveStep(typeof payload.activeStep === "number" ? payload.activeStep : 0);
        setMaxStep(typeof payload.maxStep === "number" ? payload.maxStep : 0);
        setEventCreation(typeof payload.eventCreation === "string" ? payload.eventCreation : "none");
        setCompleted(typeof payload.completed === "object" && payload.completed ? payload.completed : {});
        setStepValid(typeof payload.stepValid === "object" && payload.stepValid ? payload.stepValid : {});
        setFormData(typeof payload.formData === "object" && payload.formData ? payload.formData : initialCreatorFormData);
        setStep4SubmissionDone(Boolean(payload.step4SubmissionDone));
      } catch {
        setWorkspaceSaveError("Could not load the selected session.");
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [location.state, listMySessions]);

  React.useEffect(() => {
    if (visibleStepIds.includes(activeStep as StepId)) {
      return;
    }
    const lower = [...visibleStepIds].reverse().find((id) => id < activeStep);
    const higher = visibleStepIds.find((id) => id > activeStep);
    const fallback = visibleStepIds[0];
    setActiveStep(lower ?? higher ?? fallback);
  }, [activeStep, visibleStepIds]);

  React.useEffect(() => {
    if (step4SubmissionDone) {
      return;
    }
    if (adminMode || !hasMeaningfulCreatorData) {
      return;
    }
    const timeoutId = window.setTimeout(async () => {
      const payload = {
        activeStep,
        maxStep,
        eventCreation,
        completed,
        stepValid,
        formData,
        step4SubmissionDone,
      };
      try {
        setWorkspaceSaveBusy(true);
        setWorkspaceSaveError(null);
        if (!workspaceSessionId) {
          const created = await createSession({
            title: workspaceTitle.trim() || suggestedWorkspaceTitle,
            status: "in_progress",
            payload,
          });
          setWorkspaceSessionId(created.id);
          setWorkspaceTitle(created.title);
        } else {
          await updateSession(workspaceSessionId, {
            title: workspaceTitle.trim() || suggestedWorkspaceTitle,
            status: "in_progress",
            payload,
          });
        }
      } catch (error: any) {
        if (workspaceSessionId && error?.status === 404) {
          try {
            const recreated = await createSession({
              title: workspaceTitle.trim() || suggestedWorkspaceTitle,
              status: "in_progress",
              payload,
            });
            setWorkspaceSessionId(recreated.id);
            setWorkspaceTitle(recreated.title);
            setWorkspaceSaveError(
              "Previous session was not found anymore. A new session was created automatically."
            );
          } catch {
            setWorkspaceSaveError("Autosave failed. Changes stay local until the next retry.");
          }
        } else {
          setWorkspaceSaveError("Autosave failed. Changes stay local until the next retry.");
        }
      } finally {
        setWorkspaceSaveBusy(false);
      }
    }, 1600);
    return () => window.clearTimeout(timeoutId);
  }, [
    adminMode,
    hasMeaningfulCreatorData,
    step4SubmissionDone,
    activeStep,
    maxStep,
    eventCreation,
    completed,
    stepValid,
    formData,
    workspaceSessionId,
    workspaceTitle,
    suggestedWorkspaceTitle,
    createSession,
    updateSession,
  ]);

  const handleStep4FlowCompleted = useCallback(() => {
    flushSync(() => {
      setStep4SubmissionDone(true);
      setHasUnsavedChanges(false);
    });
    if (!workspaceSessionId) return;
    void (async () => {
      try {
        await deleteSession(workspaceSessionId);
        setWorkspaceSessionId(null);
      } catch {
        setWorkspaceSaveError("Creator finished, but deleting the session failed.");
      }
    })();
  }, [deleteSession, setHasUnsavedChanges, workspaceSessionId]);

  const discardCurrentDraft = useCallback(async () => {
    if (workspaceSessionId) {
      try {
        await deleteSession(workspaceSessionId);
      } catch {
        // Ignore deletion errors on explicit discard; local reset still prevents stale warning state.
      }
    }
    setWorkspaceSessionId(null);
    setWorkspaceSaveError(null);
    setHasUnsavedChanges(false);
    handleReset();
  }, [deleteSession, handleReset, setHasUnsavedChanges, workspaceSessionId]);

  React.useEffect(() => {
    setDiscardDraftHandler(hasMeaningfulCreatorData ? discardCurrentDraft : null);
    return () => setDiscardDraftHandler(null);
  }, [discardCurrentDraft, hasMeaningfulCreatorData, setDiscardDraftHandler]);

  const handleQuickResolvingFromCode = useCallback(
    (payload: { baseId: number; subId: number; risingCode: string }) => {
      const risingNum = parseInt(payload.risingCode, 10);
      const digits = Number.isNaN(risingNum)
        ? { baseDigit: undefined, subDigit: undefined }
        : parseRisingCodeBandDigits(risingNum);
      setFormData((prev) => ({
        ...prev,
        step1: {
          selectedBaseFunctionId: payload.baseId,
          selectedBaseDigit: digits.baseDigit,
        },
        step2: {
          selectedSubFunction: payload.subId,
          selectedSubDigit: digits.subDigit,
        },
      }));
      setEventCreation("resolving");
      setActiveStep(4);
      setMaxStep((m) => Math.max(m, 5));
      setCompleted((c) => ({ ...c, 0: true, 1: true, 2: true }));
      setStepValid((prev) => ({ ...prev, 0: true, 1: true, 2: true }));
      setQuickResolvingRisingCode(payload.risingCode);
      window.setTimeout(() => setQuickResolvingRisingCode(null), 600);
    },
    []
  );


  const handleDataChange3a = useCallback((data: any) => {
    setFormData(prev => ({ ...prev, step3a: data }));
  }, []);

  const handleValidationChangeMode = useCallback((valid: any) => {
    setStepValid((prev) => ({ ...prev, 2: valid }));
  }, []);

  const handleValidationChange3a = useCallback((valid: any) => {
    setStepValid(prev => ({ ...prev, 3: valid }));
  }, []);

  const handleDataChange3b = useCallback((data: any) => {
    setFormData(prev => ({ ...prev, step3b: data }));
  }, []);

  const handleValidationChange3b = useCallback((valid: any) => {
    setStepValid(prev => ({ ...prev, 4: valid }));
  }, []);

  const handleValidationChangeStep0 = useCallback((valid: boolean) => {
    setStepValid((prev) => (prev[0] === valid ? prev : { ...prev, 0: valid }));
  }, []);

  const handleValidationChangeStep1 = useCallback((valid: boolean) => {
    setStepValid((prev) => (prev[1] === valid ? prev : { ...prev, 1: valid }));
  }, []);

  const handleValidationChangeStep5 = useCallback((valid: boolean) => {
    setStepValid((prev) => (prev[5] === valid ? prev : { ...prev, 5: valid }));
  }, []);


  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (

          <Step1Form
            data={formData.step1}
            onValidationChange={handleValidationChangeStep0}
            onDataChange={(data) => setFormData((prev) => ({ ...prev, step1: data }))}
            onAutoContinue={() => handleNext({ bypassValidation: true })} // <— wichtig
            enteredByUser={stepEnteredByUser}
            onQuickResolvingFromCode={handleQuickResolvingFromCode}
            onOpenNumberRangeOverview={() => openNumberRangeOverview()}
          />
        );
      case 1:
        return (

          <Step2Form
            baseId={formData.step1.selectedBaseFunctionId!}
            data={formData.step2}
            onValidationChange={handleValidationChangeStep1}
            onDataChange={(data) => setFormData((prev) => ({ ...prev, step2: data }))}
            onAutoContinue={() => handleNext({ bypassValidation: true })}
          />
        );
      case 2:
        return (
          <Step3Form
            phase="mode"
            data={formData.step3a}
            eventCreation={eventCreation}
            onEventCreationChange={setEventCreation}
            onValidationChange={handleValidationChangeMode}
            onDataChange={handleDataChange3a}
            step1Data={formData.step1}
            step2Data={formData.step2}
            onOpenNumberRangeOverview={() => openNumberRangeOverview()}
          />
        );
      case 3:
        return (
          <Step3Form
            phase="rising"
            data={formData.step3a}
            eventCreation={eventCreation}
            onEventCreationChange={setEventCreation}
            onValidationChange={handleValidationChange3a}
            onDataChange={handleDataChange3a}
            step1Data={formData.step1}
            step2Data={formData.step2}
            onOpenNumberRangeOverview={() => openNumberRangeOverview()}
          />
        );
      case 4:
        return (
          <Step3Form
            phase="resolving"
            data={formData.step3b}
            eventCreation={eventCreation}
            onEventCreationChange={setEventCreation}
            onValidationChange={handleValidationChange3b}
            onDataChange={handleDataChange3b}
            step1Data={formData.step1}
            step2Data={formData.step2}
            quickResolvingRisingCode={quickResolvingRisingCode}
            risingContractName={
              eventCreation === "both"
                ? formData.step3a?.event_contract_name
                : formData.step3b?.attributes?.creator_resolving_rising_contract_name
            }
            onOpenNumberRangeOverview={(opts) => openNumberRangeOverview(opts)}
          />
        );
      case 5:
        return (
          <Step4Form
            data={formData}   // ⬅︎ GANZES OBJEKT!
            eventCreation={eventCreation}
            onValidationChange={handleValidationChangeStep5}
            risingStepValid={Boolean(stepValid[3])}
            resolvingStepValid={Boolean(stepValid[4])}
            onFlowCompleted={handleStep4FlowCompleted}
            onCreateAnotherEvent={handleCreateAnotherEvent}
          />
        );
      default:
        return null;
    }
  };

  // Daten an Django-API senden, wenn alle Schritte abgeschlossen sind
  React.useEffect(() => {
    if (allStepsCompleted()) {
      fetch('/api/events/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData.step1,
          ...formData.step2,
          ...formData.step3a,
          ...formData.step3b,
          ...formData.step4,
        }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Fehler beim Speichern');
          return res.json();
        })
        .then((data) => {
          console.log('Event gespeichert:', data);
        })
        .catch((err) => {
          console.error('Fehler:', err);
        });
    }
  }, [completed]);

  return (
    <Container maxWidth='lg' sx={{ mt: 4 }}>
      <Box
        id="meinBoxId_1"
        sx={{
          mb: 4,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
        <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
          Create a new event
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            ml: "auto",
            flex: activeStep >= 1 ? "1 1 760px" : "0 0 auto",
            justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 140,
              justifyContent: "flex-end",
              color: workspaceSaveError
                ? theme.palette.error.main
                : workspaceSaveBusy
                  ? theme.palette.warning.main
                  : theme.palette.text.secondary,
            }}
          >
            {workspaceSaveError ? (
              <ErrorOutlineIcon fontSize="small" />
            ) : workspaceSaveBusy ? (
              <CloudUploadOutlinedIcon fontSize="small" />
            ) : (
              <SaveOutlinedIcon fontSize="small" />
            )}
            <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
              {workspaceSaveError
                ? "Autosave failed"
                : workspaceSaveBusy
                  ? "Saving draft..."
                  : workspaceSessionId
                    ? "Draft saved"
                    : "Autosave active"}
            </Typography>
          </Box>
          <TextField
            size="small"
            label="Session title"
            value={workspaceTitle}
            title={workspaceTitle}
            onChange={(e) => {
              setWorkspaceTitle(e.target.value);
              setWorkspaceTitleAuto(false);
            }}
            sx={{
              minWidth: activeStep >= 1 ? 360 : 220,
              maxWidth: activeStep >= 1 ? 620 : 320,
              width: activeStep >= 1 ? "100%" : "auto",
              "& .MuiInputBase-input": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>

        {activeStep < 1 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, ml: 3 }}>
          <FormControlLabel
            control={
              <Switch
                disabled={!isAdmin}
                checked={adminMode}
                onChange={() => {
                  if (isAdmin)
                    setAdminMode(prev => !prev);
                }}
                sx={{ color: theme.palette.text.primary }}
              />
            }
            label="Admin Mode"
            sx={{ color: theme.palette.text.primary }}
          />
          <Box display="flex" alignItems="center">
            {!isAdmin &&
              <Tooltip title="Required role for this action: EM-Admin">
                <LockIcon fontSize="small" style={{ marginLeft: 0, color: theme.palette.text.primary }} />
              </Tooltip>
            }
          </Box>
        </Box>}
        </Box>

      </Box>
      {workspaceSaveError && (
        <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
          {workspaceSaveError}
        </Typography>
      )}


      {adminMode ? (
        // Admin-Modus-Inhalt
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
            Admin Mode active
          </Typography>
          <CreateEventAdmin />
        </Box>
      ) : (
        // Normaler Stepper-Modus
        <>
          <Box id="meinBoxId_2" sx={{ mt: 4 }}>
            <Stepper id="meinBoxId_3" nonLinear activeStep={activeVisibleIndex}>
              {visibleStepIds.map((stepId) => {
                const label = steps[stepId];
                const index = stepId;
                const visibleIndex = visibleStepIds.indexOf(stepId);
                return (
                <Step id="meinBoxId_4" key={`${label}-${index}`} completed={completed[index]}>
                  <StepButton
                    color="inherit"
                    onClick={handleStep(index)}
                    disabled={
                      visibleIndex > maxVisibleStep ||
                      (step4SubmissionDone && activeStep === 5 && index !== 5)
                    }
                  >
                    {STEPPER_LABEL_TOOLTIPS[index] ? (
                      <Tooltip title={STEPPER_LABEL_TOOLTIPS[index]!} enterDelay={400}>
                        <span>{label}</span>
                      </Tooltip>
                    ) : (
                      label
                    )}
                  </StepButton>
                </Step>
                );
              })}
            </Stepper>
          </Box>

          <Box
            id="meinBoxId_5"
            key={wizardSessionKey}
            sx={{ mt: 3, pt: 1, display: "flex", justifyContent: "center" }}
          >
            {renderStepContent(activeStep)}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr auto 1fr" },
              alignItems: "center",
              gap: 1.5,
              pt: 2,
              width: "100%",
            }}
          >
            <Box sx={{ justifySelf: "start", gridColumn: 1, gridRow: 1 }}>
              {activeStep !== 0 && !(activeStep === 5 && step4SubmissionDone) && (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    borderRadius: 6,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Back
                </Button>
              )}
            </Box>

            {contextBaseDigit != null && activeStep >= 1 && activeStep < 3 && (
              <Box
                sx={{
                  justifySelf: "center",
                  width: { xs: "100%", sm: "auto" },
                  maxWidth: "100%",
                  gridColumn: { xs: "1 / -1", sm: 2 },
                  gridRow: { xs: 2, sm: 1 },
                }}
              >
                <CreatorContextBandBar
                  layout="horizontal"
                  baseName={contextBaseName}
                  subName={contextSubName}
                  baseDigit={contextBaseDigit}
                  subDigit={contextSubDigit}
                  eventCreation={eventCreation}
                />
              </Box>
            )}

            <Box
              sx={{
                justifySelf: "end",
                gridColumn: { xs: 2, sm: 3 },
                gridRow: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {activeStep > 1 && activeStep !== 5 && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNext()}
                  disabled={!stepValid[activeStep]}
                  sx={{
                    borderRadius: 6,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Continue
                </Button>
              )}
            </Box>
          </Box>

          {allStepsCompleted() && (
            <Box sx={{ mt: 2 }}>
              <Typography>Alle Schritte abgeschlossen – Konfiguration fertig!</Typography>
              <Button onClick={handleReset}>Reset</Button>
            </Box>
          )}
        </>
      )}

      <NumberRangeOverviewDialog
        open={numberRangeOverviewOpen}
        onClose={() => setNumberRangeOverviewOpen(false)}
        highlightBaseId={formData.step1?.selectedBaseFunctionId ?? null}
        highlightSubId={(formData.step2 as { selectedSubFunction?: number })?.selectedSubFunction ?? null}
        initialLookupCode={overviewLookupCode}
        filterCurrentSelectionOnly={false}
      />
    </Container>
  );
}